import React, { Component, Fragment } from 'react';
import { PropTypes } from 'prop-types';
import { withRouter } from 'react-router';
import { isEmpty } from 'lodash';
import { Trans } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import { H3, QuickActionButton } from 'base-components';
import { Row, Column } from 'styled-components-reactive-grid';
import { px2rem } from 'decisiv-ui-utils';

import Panel from 'blocks/Panel';
import useContext from 'utils/useContext';
import ButtonLinkWithIcon from 'elements/ButtonLinkWithIcon';

import AfterHoursContact from './AfterHoursContact';
import NoContactsMessage from './NoContactsMessage';
import StoreContactsModal from './StoreContactsModal';
import DeleteContactModal from './DeleteContactModal';
import withContacts from './withContacts';
import DealerDetailsContext from '../DealerDetailsContext';

function CreateContactButton({ onClick }) {
  return (
    <QuickActionButton onClick={onClick}>
      <QuickActionButton.Icon modifiers={['left']} name="plus" />
      <QuickActionButton.Text>
        <Trans>Create Contact</Trans>
      </QuickActionButton.Text>
    </QuickActionButton>
  );
}

CreateContactButton.propTypes = { onClick: PropTypes.func.isRequired };

export class Contacts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayStoreContactsModal: false,
      isEditing: false,
      displayDeleteContactModal: false,
      selectedContact: null,
    };
  }

  toggleModal = ({ isEditing, contact = {} }) =>
    this.setState({
      displayStoreContactsModal: !this.state.displayStoreContactsModal,
      isEditing,
      selectedContact: contact,
    });

  toggleCreateModal = () => this.toggleModal({ isEditing: false });

  toggleEditModal = contact => this.toggleModal({ isEditing: true, contact });

  toggleDeleteModal = contact =>
    this.setState({
      displayDeleteContactModal: !this.state.displayDeleteContactModal,
      selectedContact: contact,
    });

  render() {
    const {
      displayStoreContactsModal,
      displayDeleteContactModal,
      isEditing,
      selectedContact,
    } = this.state;

    const { contacts, isReadOnly, dealerId } = this.props;

    return (
      <Panel modifiers={['padScale_0']}>
        {/*
        The padding around this Panel is removed because the individual
        contact selectables need to span the full width of the Panel

        Padding will be applied instead to each row in the Panel.
        */}
        <Row modifiers={['padScaleX_4', 'padScaleY_3']}>
          <Column modifiers={['col']}>
            <H3 modifiers={['fontWeightRegular']}>
              <Trans>Contacts</Trans>
              {contacts && contacts.length ? ` (${contacts.length})` : ''}
            </H3>
          </Column>
        </Row>

        {isEmpty(contacts) && (
          <Fragment>
            <NoContactsMessage />
            {/*
            Add extra bottom padding to compensate for the fact that
            the containing Panel's padding was removed.
            */}
            <Row modifiers={['center']} style={{ paddingBottom: px2rem(16) }}>
              {!isReadOnly && (
                <Column>
                  <CreateContactButton onClick={this.toggleCreateModal} />
                </Column>
              )}
            </Row>
          </Fragment>
        )}

        {isEmpty(contacts) || (
          <Fragment>
            <Row
              style={{
                maxHeight: '17rem',
                overflowY: 'auto',
              }}
            >
              <Column modifiers={['col', 'padScale_0']}>
                {contacts.map(contact => (
                  <AfterHoursContact
                    key={contact.id}
                    contact={contact}
                    isReadOnly={isReadOnly}
                    toggleEditContact={this.toggleEditModal}
                    toggleDeleteContact={this.toggleDeleteModal}
                  />
                ))}
              </Column>
            </Row>
            <Row modifiers={['padScaleX_4', 'padScaleY_3']}>
              {!isReadOnly && (
                <Column>
                  <ButtonLinkWithIcon
                    icon="plus"
                    onClick={this.toggleCreateModal}
                  >
                    <Trans>Create Contact</Trans>
                  </ButtonLinkWithIcon>
                </Column>
              )}
            </Row>
          </Fragment>
        )}

        {displayStoreContactsModal && (
          <StoreContactsModal
            isEditing={isEditing}
            onClose={this.toggleCreateModal}
            selectedContact={selectedContact}
          />
        )}

        {displayDeleteContactModal && (
          <DeleteContactModal
            onClose={this.toggleDeleteModal}
            contact={selectedContact}
            contacts={contacts}
            dealerId={dealerId}
          />
        )}
      </Panel>
    );
  }
}

Contacts.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({})),
  isReadOnly: PropTypes.bool.isRequired,
  // these are used in DeleteContactModal
  dealerId: PropTypes.string.isRequired,
};

Contacts.defaultProps = {
  contacts: [],
};

export default compose(
  setDisplayName('Contacts'),
  withRouter,
  withContacts,
  useContext(DealerDetailsContext),
)(Contacts);



// WEBPACK FOOTER //
// ./src/compositions/TechAdmin/DealerDetails/Contacts/index.js